import { useEffect, useRef, useState } from 'react';
import { Camera, Crop, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { api, message } from '../../lib/api';
import { PhotoEditor } from './PhotoEditor';
import { BLOCKED_FILE_MESSAGE, blockedFailure, photoSourceDetail, photoSourceMessage, prepareEditorSource } from '../../lib/photoSource';

const FailureToast = ({ text, detail }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="photo-failure-toast">
      <div>{text}</div>
      {detail && <button type="button" className="photo-failure-details-toggle" data-testid="photo-failure-details-toggle" onClick={() => setOpen(v => !v)}>{open ? 'Hide details' : 'Details'}</button>}
      {open && <div className="photo-failure-details" data-testid="photo-failure-details">{detail}</div>}
    </div>
  );
};
const failToast = (text, detail) => toast.error(<FailureToast text={text} detail={detail} />, { duration: 12000 });

export const PhotoUpload = ({ photoId, purpose, resourceId, onUpload, id = 'photo-upload' }) => {
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const source = editor?.source;
  useEffect(() => () => { if (source?.startsWith('blob:')) URL.revokeObjectURL(source); }, [source]);

  const choose = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setLoading(true);
    try {
      const { source, uploadFile } = await prepareEditorSource(file);
      setEditor({ source, file: uploadFile, photoId: null, adjustment: null });
    } catch (err) {
      if (err.blocked) { await serverFirst(file, err); return; }
      failToast(photoSourceMessage(err), photoSourceDetail(err));
    } finally { setLoading(false); }
  };
  // In-app browsers (e.g. the Preview window on Android) can deny the page any
  // read of a gallery file while still letting the browser stream it in a form
  // post. Upload the untouched file, then open the editor from the server copy.
  const serverFirst = async (file, readError) => {
    try {
      const form = new FormData(); form.append('file', file, file.name || 'photo.jpg');
      const uploaded = await api.post('/uploads', form, { params: { purpose, resource_id: resourceId } });
      const original = await api.get(`/photos/${uploaded.data.id}/original`, { responseType: 'blob' });
      const { source } = await prepareEditorSource(original.data);
      setEditor({ source, file: null, photoId: uploaded.data.id, adjustment: null });
    } catch (e) {
      if (e.response) { failToast(`${photoSourceMessage(readError)} Sending it to the server also failed: ${message(e)}`, photoSourceDetail(readError)); return; }
      const blocked = blockedFailure(e);
      failToast(BLOCKED_FILE_MESSAGE, `${photoSourceDetail(readError)} · upload:${blocked.detail}`);
    }
  };
  const adjust = async () => {
    setLoading(true); setError('');
    try {
      const [info, original] = await Promise.all([api.get(`/photos/${photoId}/editor`), api.get(`/photos/${photoId}/original`, { responseType: 'blob' })]);
      const { source } = await prepareEditorSource(original.data);
      setEditor({ source, file: null, photoId, adjustment: info.data.adjustment });
    } catch (e) { toast.error(message(e)); }
    finally { setLoading(false); }
  };
  const save = async adjustment => {
    setBusy(true); setError('');
    try {
      let sourceId = editor.photoId;
      if (!sourceId) {
        const form = new FormData(); form.append('file', editor.file);
        const result = await api.post('/uploads', form, { params: { purpose, resource_id: resourceId } });
        sourceId = result.data.id;
        setEditor(current => ({ ...current, photoId: sourceId }));
      }
      const result = await api.post(`/photos/${sourceId}/crop`, adjustment);
      onUpload(result.data.id);
      setEditor(null);
      toast.success('Photo ready. Save your profile to keep the change.');
    } catch (e) {
      setError(e.response ? message(e) : BLOCKED_FILE_MESSAGE);
    }
    finally { setBusy(false); }
  };

  return (
    <div className="photo-upload-controls">
      <input className="photo-file-input" ref={fileInput} data-testid={id} type="file" accept="image/*" onChange={choose} disabled={loading || busy} aria-label="Choose profile photo" />
      <button className="photo-action-button" type="button" data-testid={`${id}-button`} disabled={loading || busy} onClick={() => fileInput.current?.click()}><Upload size={15} />{photoId ? 'Replace photo' : 'Upload photo'}</button>
      <input className="photo-file-input" ref={cameraInput} data-testid={`${id}-camera`} type="file" accept="image/*" capture="environment" onChange={choose} disabled={loading || busy} aria-label="Take a photo with your camera" />
      <button className="photo-action-button" type="button" data-testid={`${id}-camera-button`} disabled={loading || busy} onClick={() => cameraInput.current?.click()}><Camera size={15} />Take photo</button>
      {photoId && <button className="photo-action-button" type="button" data-testid={`${id}-adjust`} disabled={loading || busy} onClick={adjust}>{loading ? <Loader2 size={15} className="animate-spin" /> : <Crop size={15} />}{loading ? 'Opening…' : 'Adjust photo'}</button>}
      {photoId && <button className="photo-icon-button photo-remove-button" type="button" data-testid={`${id}-remove`} title="Remove profile photo" disabled={loading || busy} onClick={() => onUpload(null)}><Trash2 size={15} /></button>}
      {editor && <PhotoEditor source={editor.source} initial={editor.adjustment} busy={busy} error={error} purpose={purpose} onClose={() => setEditor(null)} onSave={save} />}
    </div>
  );
};